import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    useTheme,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CommonCard from '../../../../components/card/CommonCard';
import RadarChart from '../../../../components/charts/radarChart';
import Toggle from '../../../../components/toggle';
import { DidimIcon, CaloryIcon, TimeIcon } from '../../../../assets/images/activityIcons';
import DonutChart from '../../../../components/charts/donutChart';
import { EXERCISE_TARGETS } from '../../../../constants';
import { calculatePercentage, formatDateTime, formatDateTimeDisplay } from '../../../../utils/base';
import WeekCalendar from '../../../../components/charts/donutChart/weeklyCalendar';
import Recommendation from '../../../../components/recommendation';
import ChartSection from '../../../../components/charts/chartSection';
import BarChart from '../../../../components/charts/barChart';
import { contentAPI, physicalRecordAPI, logAPI } from '../../../../actions/imsServer';
import DateIndicator from '../../../../components/calendar/DateIndicator';
import StatsSection from '../../../../components/statsSection';
import StatisticsChart from '../../../../components/charts/barChart/StatisticsChart';
import DataItem from '../../../../components/dataItem';
import ActivityCard from '../../../../components/activityCard';
import { CHALLENGE_HEARTBEAT_MAX, API_PAGE_SIZE } from '../../../../constants';

export const DidimActivityTab = ({
    userData,
    playLogsData = [],
    selectedOrganization: propSelectedOrganization
}) => {

    const theme = useTheme();
    const { t } = useTranslation();
    const [activeView, setActiveView] = useState(0); // 0: 일간, 1: 주간, 2: 월간, 3: 연간
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });
    const [selectedOrganization, setSelectedOrganization] = useState(propSelectedOrganization || '');
    const [selectedHourIndex, setSelectedHourIndex] = useState(-1);
    const [contents, setContents] = useState([]);
    const [yesterdayPhysicalRecord, setYesterdayPhysicalRecord] = useState(null);
    const [localPlayLogsData, setLocalPlayLogsData] = useState(playLogsData || []);
    const popupHeight = 27;

    // 주간/월간/연간 인덱스 상태 관리
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
    const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
    const [currentYearIndex, setCurrentYearIndex] = useState(0);
    const [selectedBarIndex, setSelectedBarIndex] = useState(-1);

    // propSelectedOrganization이 변경되면 내부 상태 업데이트
    useEffect(() => {
        if (propSelectedOrganization !== undefined) {
            setSelectedOrganization(propSelectedOrganization);
        }
    }, [propSelectedOrganization]);

    // playLogsData prop이 변경되면 로컬 상태 업데이트 (병합)
    useEffect(() => {
        if (playLogsData && playLogsData.length > 0) {
            setLocalPlayLogsData(prevLogs => {
                if (!prevLogs || prevLogs.length === 0) {
                    return playLogsData;
                }
                // 기존 데이터와 병합 (중복 제거)
                const existingIds = new Set(prevLogs.map(log => log.id));
                const uniqueNewLogs = playLogsData.filter(log => !existingIds.has(log.id));
                return [...prevLogs, ...uniqueNewLogs].sort((a, b) => {
                    const dateA = new Date(a.time || a.startTime);
                    const dateB = new Date(b.time || b.startTime);
                    return dateA - dateB;
                });
            });
        } else if (playLogsData === null || playLogsData.length === 0) {
            // prop이 비어있으면 초기화하지 않고 기존 데이터 유지 (일간 뷰에서 스크롤 중일 수 있음)
            // setLocalPlayLogsData([]);
        }
    }, [playLogsData]);

    // 주간/월간/연간 인덱스가 변경될 때 selectedDate 업데이트
    useEffect(() => {
        if (activeView === 1) {
            // 주간: currentWeekIndex에 따라 날짜 계산
            const today = new Date();
            const startOfWeek = new Date(today);
            const dayOfWeek = today.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            startOfWeek.setDate(today.getDate() + mondayOffset);
            startOfWeek.setDate(startOfWeek.getDate() + currentWeekIndex * 7);
            startOfWeek.setHours(0, 0, 0, 0);
            setSelectedDate(startOfWeek);
        } else if (activeView === 2) {
            // 월간: currentMonthIndex에 따라 날짜 계산
            const today = new Date();
            const targetMonth = new Date(today);
            targetMonth.setMonth(today.getMonth() + currentMonthIndex);
            targetMonth.setDate(1);
            targetMonth.setHours(0, 0, 0, 0);
            setSelectedDate(targetMonth);
        } else if (activeView === 3) {
            // 연간: currentYearIndex에 따라 날짜 계산
            const today = new Date();
            const targetYear = new Date(today);
            targetYear.setFullYear(today.getFullYear() + currentYearIndex);
            targetYear.setMonth(0);
            targetYear.setDate(1);
            targetYear.setHours(0, 0, 0, 0);
            setSelectedDate(targetYear);
        }
    }, [activeView, currentWeekIndex, currentMonthIndex, currentYearIndex]);

    // 날짜가 변경될 때 해당 날짜 범위의 데이터 가져오기
    useEffect(() => {
        const fetchPlayLogsByDate = async () => {
            if (!userData?.id) return;

            try {
                let startTime;
                let endTime;

                if (activeView === 0) {
                    // 일간: 오늘부터 과거 3개월(90일) 데이터 가져오기
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    const startDate = new Date(today);
                    startDate.setDate(today.getDate() - 89); // 과거 3개월 (90일)
                    startDate.setHours(0, 0, 0, 0);
                    startTime = startDate.toISOString();
                    endTime = today.toISOString();
                } else if (activeView === 1) {
                    // 주간: 선택된 날짜가 포함된 주의 시작일과 종료일
                    const currentDay = selectedDate.getDay();
                    const startOfWeek = new Date(selectedDate);
                    startOfWeek.setDate(selectedDate.getDate() - currentDay);
                    startOfWeek.setHours(0, 0, 0, 0);
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    endOfWeek.setHours(23, 59, 59, 999);
                    startTime = startOfWeek.toISOString();
                    endTime = endOfWeek.toISOString();
                } else if (activeView === 2) {
                    // 월간: 선택된 날짜가 포함된 월의 시작일과 종료일
                    const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                    startOfMonth.setHours(0, 0, 0, 0);
                    const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
                    endOfMonth.setHours(23, 59, 59, 999);
                    startTime = startOfMonth.toISOString();
                    endTime = endOfMonth.toISOString();
                } else if (activeView === 3) {
                    // 연간: 선택된 날짜가 포함된 연도의 시작일과 종료일
                    const startOfYear = new Date(selectedDate.getFullYear(), 0, 1);
                    startOfYear.setHours(0, 0, 0, 0);
                    const endOfYear = new Date(selectedDate.getFullYear(), 11, 31);
                    endOfYear.setHours(23, 59, 59, 999);
                    startTime = startOfYear.toISOString();
                    endTime = endOfYear.toISOString();
                }

                let allLogs = [];
                let offset = 0;
                let page;
                do {
                    const response = await logAPI.getPlayLogs({
                        userId: userData.id,
                        startTime,
                        endTime,
                        expand: 'organization',
                        offset
                    });
                    page = response?.data?.logs || [];
                    allLogs = allLogs.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);

                if (allLogs.length > 0) {
                    // 기존 데이터와 병합 (중복 제거)
                    setLocalPlayLogsData(prevLogs => {
                        if (!prevLogs || prevLogs.length === 0) {
                            return allLogs;
                        }
                        const existingIds = new Set(prevLogs.map(log => log.id));
                        const uniqueNewLogs = allLogs.filter(log => !existingIds.has(log.id));
                        return [...prevLogs, ...uniqueNewLogs].sort((a, b) => {
                            const dateA = new Date(a.time || a.startTime);
                            const dateB = new Date(b.time || b.startTime);
                            return dateA - dateB;
                        });
                    });
                } else {
                    // 데이터가 없어도 기존 데이터 유지 (일간 뷰에서만)
                    if (activeView !== 0) {
                        setLocalPlayLogsData([]);
                    }
                }
            } catch (err) {
                console.error('플레이 로그 데이터 가져오기 실패:', err);
                // 에러 발생 시 기존 데이터 유지
            }
        };

        fetchPlayLogsByDate();
    }, [userData?.id, selectedDate, activeView]);

    // 선택된 기관의 playLogsData만 필터링
    const filteredPlayLogsData = useMemo(() => {
        if (!selectedOrganization || !localPlayLogsData || localPlayLogsData.length === 0) {
            return localPlayLogsData || [];
        }
        return localPlayLogsData.filter(log =>
            String(log.organizationId) === String(selectedOrganization)
        );
    }, [localPlayLogsData, selectedOrganization]);

    // 선택된 날짜의 운동 활동 리스트 필터링
    const selectedDateActivityList = useMemo(() => {
        if (!filteredPlayLogsData || filteredPlayLogsData.length === 0) {
            return [];
        }

        const selectedDateStr = formatDateTime(selectedDate, '-', ':', 0, 10); // YYYY-MM-DD 형식

        return filteredPlayLogsData.filter(log => {
            if (!log.time) return false;
            const logDate = new Date(log.time);
            const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
            return logDateStr === selectedDateStr;
        });
    }, [filteredPlayLogsData, selectedDate]);

    // 콘텐츠 정보 가져오기
    useEffect(() => {
        const fetchContents = async () => {
            if (!filteredPlayLogsData || filteredPlayLogsData.length === 0) {
                setContents([]);
                return;
            }

            try {
                // filteredPlayLogsData에서 고유한 contentIndex 추출
                const uniqueContentIndexes = [...new Set(
                    filteredPlayLogsData
                        .map(log => log.contentIndex)
                        .filter(index => index != null)
                )];

                if (uniqueContentIndexes.length > 0) {
                    const contentIndexStr = uniqueContentIndexes.join(',');
                    const response = await contentAPI.getContents({
                        index: contentIndexStr,
                        state: 0
                    });

                    if (response?.data?.contents) {
                        setContents(response.data.contents);
                    } else {
                        setContents([]);
                    }
                } else {
                    setContents([]);
                }
            } catch (err) {
                console.error('콘텐츠 정보 로드 실패:', err);
                setContents([]);
            }
        };

        fetchContents();
    }, [filteredPlayLogsData]);

    // 어제 날짜의 physicalRecord 가져오기
    useEffect(() => {
        const fetchYesterdayPhysicalRecord = async () => {
            if (!userData?.id) return;

            try {
                // 로컬 시간대의 어제 날짜 계산
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const year = yesterday.getFullYear();
                const month = String(yesterday.getMonth() + 1).padStart(2, '0');
                const day = String(yesterday.getDate()).padStart(2, '0');
                const yesterdayStr = `${year}-${month}-${day}`;

                const response = await physicalRecordAPI.getPhysicalRecords({
                    userId: userData.id,
                    state: 0,
                    startDate: yesterdayStr,
                    endDate: yesterdayStr
                });

                if (response?.data?.physicalRecords && response.data.physicalRecords.length > 0) {
                    setYesterdayPhysicalRecord(response.data.physicalRecords[0]);
                } else {
                    setYesterdayPhysicalRecord(null);
                }
            } catch (err) {
                console.error('어제 physicalRecord 로딩 실패:', err);
                setYesterdayPhysicalRecord(null);
            }
        };

        fetchYesterdayPhysicalRecord();
    }, [userData?.id]);

    // 주간 데이터 생성
    const generateThisWeek = () => {
        // 선택된 날짜를 기반으로 주간 계산
        const currentDay = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Get start of week (Sunday) based on selectedDate
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(selectedDate.getDate() - currentDay);

        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];
        const week = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);

            // 날짜 비교 시 시간 부분 제거
            const dateForCompare = new Date(date);
            dateForCompare.setHours(0, 0, 0, 0);
            const selectedDateForCompare = new Date(selectedDate);
            selectedDateForCompare.setHours(0, 0, 0, 0);
            const isSelected = dateForCompare.getTime() === selectedDateForCompare.getTime();

            // 해당 날짜의 실제 데이터 찾기 (이미 필터링된 filteredPlayLogsData 사용)
            const dateStr = date.toISOString().split('T')[0];

            const dayRecords = filteredPlayLogsData?.filter(record =>
                record.time && new Date(record.time).toISOString().split('T')[0] === dateStr
            ) || [];

            // 데이터 합산
            const dayTotals = dayRecords.reduce((acc, record) => {
                return {
                    totalSteps: acc.totalSteps + (record.stepCount || 0),
                    calories: acc.calories + (Math.max(0, (record.endCalories || 0) - (record.startCalories || 0))),
                    totalTime: acc.totalTime + (Math.max(0, (record.endPlayTime || 0) - (record.startPlayTime || 0)))
                };
            }, {
                totalSteps: 0,
                calories: 0,
                totalTime: 0
            });

            // EXERCISE_TARGETS 값 확인
            const stepsTarget = EXERCISE_TARGETS?.STEPS || 6000;
            const caloriesTarget = EXERCISE_TARGETS?.CALORIES || 500;
            const timeTarget = (EXERCISE_TARGETS?.ACTIVITY_TIME || 90) * 60; // 초 단위

            // 안전한 퍼센트 계산 함수
            const safeCalculatePercentage = (current, target) => {
                if (!target || target === 0) {
                    console.warn('safeCalculatePercentage: target is 0 or undefined', { current, target });
                    return 0;
                }
                if (current === undefined || current === null) {
                    console.warn('safeCalculatePercentage: current is undefined or null', { current, target });
                    return 0;
                }
                const percentage = Math.min(Math.round((current / target) * 100), 100);
                if (isNaN(percentage)) {
                    console.warn('safeCalculatePercentage: result is NaN', { current, target, percentage });
                    return 0;
                }
                return percentage;
            };

            const stepsValue = safeCalculatePercentage(dayTotals.totalSteps, stepsTarget);
            const caloriesValue = safeCalculatePercentage(dayTotals.calories, caloriesTarget);
            const timeValue = safeCalculatePercentage(dayTotals.totalTime, timeTarget);

            const chartData = [
                {
                    label: t('didim'),
                    value: stepsValue,
                    color: theme.palette.figma.didim,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    label: t('calories'),
                    value: caloriesValue,
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    label: t('time'),
                    value: timeValue,
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];

            week.push({
                day: dayNames[i],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
                hasData: dayRecords && dayRecords.length > 0
            });

            //  console.log('week', week);
        }

        return week;
    };

    const days = useMemo(() => generateThisWeek(), [selectedDate, filteredPlayLogsData, theme.palette.figma, selectedOrganization, generateThisWeek]);

    const handleDayClick = (date) => {
        if (date) {
            const newDate = new Date(date);
            newDate.setHours(0, 0, 0, 0);
            setSelectedDate(newDate);
        }
    };

    const _handleOrganizationChange = (value) => setSelectedOrganization(value);
    void _handleOrganizationChange;

    const handleWeekChange = (newDate) => {
        setSelectedDate(newDate);
    };

    // 선택된 바를 해제하는 함수
    const clearSelectedBar = () => {
        setSelectedBarIndex(-1);
    };

    // filteredPlayLogsData에서 날짜별 디딤 수 집계 함수
    const getDidimCountByDate = (dateStr) => {
        if (!filteredPlayLogsData || filteredPlayLogsData.length === 0) return 0;

        const logs = filteredPlayLogsData.filter(log => {
            const logDate = new Date(log.time);
            const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
            return logDateStr === dateStr;
        });

        return logs.reduce((sum, log) => sum + (log.stepCount || 0), 0);
    };

    // 현재 주간 데이터 가져오기
    const getCurrentWeekData = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        const weekData = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);
            const didimCount = getDidimCountByDate(dateStr);

            weekData.push({
                day: dateStr,
                value: didimCount,
                color: didimCount >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.didim : theme.palette.figma.white[40]
            });
        }
        return weekData;
    };

    // 현재 주간 통계 계산
    const calculateCurrentWeekStats = () => {
        const currentWeekData = getCurrentWeekData();
        if (currentWeekData.length === 0) {
            return {
                value: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0
            };
        }

        const totals = currentWeekData.reduce((acc, day) => {
            acc.totalSteps += day.value;
            if (day.value >= EXERCISE_TARGETS.STEPS) {
                acc.goalDays++;
            }
            return acc;
        }, { totalSteps: 0, goalDays: 0 });

        const averageSteps = Math.round(totals.totalSteps / 7);

        return {
            value: averageSteps,
            totalSteps: totals.totalSteps,
            goalDays: totals.goalDays,
            totalDays: 7
        };
    };

    // 현재 월간 데이터 가져오기
    const getCurrentMonthData = () => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const monthData = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);
            const didimCount = getDidimCountByDate(dateStr);

            monthData.push({
                day: dateStr,
                value: didimCount,
                color: didimCount >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.didim : theme.palette.figma.white[40]
            });
        }
        return monthData;
    };

    // 현재 월간 통계 계산
    const calculateCurrentMonthStats = () => {
        const monthData = getCurrentMonthData();
        if (monthData.length === 0) {
            return {
                value: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0
            };
        }

        const totals = monthData.reduce((acc, day) => {
            acc.totalSteps += day.value;
            if (day.value >= EXERCISE_TARGETS.STEPS) {
                acc.goalDays++;
            }
            return acc;
        }, { totalSteps: 0, goalDays: 0 });

        const averageSteps = Math.round(totals.totalSteps / monthData.length);

        return {
            value: averageSteps,
            totalSteps: totals.totalSteps,
            goalDays: totals.goalDays,
            totalDays: monthData.length
        };
    };

    // 현재 연간 데이터 가져오기
    const getCurrentYearData = () => {
        const today = new Date();
        const targetYear = today.getFullYear() + currentYearIndex;

        const yearData = [];
        for (let month = 0; month < 12; month++) {
            const monthStr = `${targetYear}-${String(month + 1).padStart(2, '0')}`;

            // 해당 월의 모든 filteredPlayLogsData 합산
            const monthLogs = filteredPlayLogsData?.filter(log => {
                const logDate = new Date(log.time || log.startTime);
                const logMonthStr = formatDateTime(logDate, '-', ':', 0, 7);
                return logMonthStr === monthStr;
            }) || [];

            const monthTotals = monthLogs.reduce((acc, log) => {
                return {
                    totalSteps: acc.totalSteps + (log.stepCount || 0),
                    goalDays: acc.goalDays + ((log.stepCount || 0) >= EXERCISE_TARGETS.STEPS ? 1 : 0)
                };
            }, { totalSteps: 0, goalDays: 0 });

            const averageSteps = monthTotals ? Math.round(monthTotals.totalSteps / 30) : 0;

            yearData.push({
                day: `${targetYear}-${String(month + 1).padStart(2, '0')}`,
                value: averageSteps,
                stepsTotal: monthTotals?.totalSteps || 0,
                goalDays: monthTotals?.goalDays || 0,
                color: averageSteps >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.didim : theme.palette.figma.white[40]
            });
        }
        return yearData;
    };

    // 현재 연간 통계 계산
    const calculateCurrentYearStats = () => {
        const yearData = getCurrentYearData();
        if (yearData.length === 0) {
            return {
                value: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0,
                bestDay: 0,
                worstDay: 0
            };
        }

        const totals = yearData.reduce((acc, month) => {
            acc.totalSteps += month.stepsTotal;
            acc.goalDays += month.goalDays;
            return acc;
        }, { totalSteps: 0, goalDays: 0 });

        const averageSteps = Math.round(totals.totalSteps / 12);

        const bestMonth = yearData.reduce((best, month) =>
            month.value > best.value ? month : best, yearData[0]);
        const worstMonth = yearData.reduce((worst, month) =>
            month.value < worst.value ? month : worst, yearData[0]);

        return {
            value: averageSteps,
            totalSteps: totals.totalSteps,
            goalDays: totals.goalDays,
            totalDays: 12,
            bestDay: bestMonth.value,
            worstDay: worstMonth.value
        };
    };

    // 주간 제목 생성 (다국어 지원)
    const getWeeklyTitle = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        const startFormatted = formatDateTimeDisplay(startOfWeek, 'monthDay');
        const endFormatted = formatDateTimeDisplay(endOfWeek, 'monthDay');
        return `${startFormatted} - ${endFormatted}`;
    };

    // 월간 제목 생성 (다국어 지원)
    const getMonthlyTitle = () => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        return formatDateTimeDisplay(targetMonth, 'yearMonth');
    };

    // 연간 제목 생성 (다국어 지원)
    const getYearlyTitle = () => {
        const today = new Date();
        const targetYear = new Date(today.getFullYear() + currentYearIndex, 0, 1);
        return formatDateTimeDisplay(targetYear, 'year');
    };

    // 주간 이동 함수들
    const handlePreviousWeek = () => {
        setCurrentWeekIndex(currentWeekIndex - 1);
        clearSelectedBar();
    };

    const handleNextWeek = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset);

        const nextWeekStart = new Date(startOfWeek);
        nextWeekStart.setDate(startOfWeek.getDate() + (currentWeekIndex + 1) * 7);

        if (nextWeekStart <= today) {
            setCurrentWeekIndex(currentWeekIndex + 1);
            clearSelectedBar();
        }
    };

    // 월간 이동 함수들
    const handlePreviousMonth = () => {
        setCurrentMonthIndex(currentMonthIndex - 1);
        clearSelectedBar();
    };

    const handleNextMonth = () => {
        const today = new Date();
        const currentMonth = new Date(today);
        currentMonth.setMonth(today.getMonth() + currentMonthIndex + 1);

        if (currentMonth <= today) {
            setCurrentMonthIndex(currentMonthIndex + 1);
            clearSelectedBar();
        }
    };

    // 연간 이동 함수들
    const handlePreviousYear = () => {
        setCurrentYearIndex(currentYearIndex - 1);
        clearSelectedBar();
    };

    const handleNextYear = () => {
        const today = new Date();
        const currentYear = new Date(today);
        currentYear.setFullYear(today.getFullYear() + currentYearIndex + 1);

        if (currentYear <= today) {
            setCurrentYearIndex(currentYearIndex + 1);
            clearSelectedBar();
        }
    };

    // 주간/월간/연간 뷰 렌더링
    const renderRangeView = () => {
        let stats, chartData, yAxisData, title, activeViewStr, onPrevious, onNext;

        if (activeView === 1) {
            // 주간 뷰
            stats = calculateCurrentWeekStats();
            chartData = getCurrentWeekData().map(item => ({
                day: item.day,
                value: item.value,
                color: item.color
            }));
            yAxisData = {
                maxValue: Math.max(...getCurrentWeekData().map(item => item.value), 1),
                minValue: 0,
                averageValue: stats.value,
                yAxisValues: [0, Math.max(...getCurrentWeekData().map(item => item.value), 1) / 2, Math.max(...getCurrentWeekData().map(item => item.value), 1)]
            };
            title = getWeeklyTitle();
            activeViewStr = "week";
            onPrevious = handlePreviousWeek;
            onNext = handleNextWeek;
        } else if (activeView === 2) {
            // 월간 뷰
            stats = calculateCurrentMonthStats();
            chartData = getCurrentMonthData().map(item => ({
                day: item.day,
                value: item.value,
                color: item.color
            }));
            yAxisData = {
                maxValue: Math.max(...getCurrentMonthData().map(item => item.value), 1),
                minValue: 0,
                averageValue: stats.value,
                yAxisValues: [0, Math.max(...getCurrentMonthData().map(item => item.value), 1) / 2, Math.max(...getCurrentMonthData().map(item => item.value), 1)]
            };
            title = getMonthlyTitle();
            activeViewStr = "month";
            onPrevious = handlePreviousMonth;
            onNext = handleNextMonth;
        } else if (activeView === 3) {
            // 연간 뷰
            stats = calculateCurrentYearStats();
            chartData = getCurrentYearData().map(item => ({
                day: item.day,
                value: item.value,
                stepsTotal: item.stepsTotal,
                goalDays: item.goalDays,
                color: item.color
            }));
            yAxisData = {
                maxValue: Math.max(...getCurrentYearData().map(item => item.value), 1),
                minValue: 0,
                averageValue: stats.value,
                yAxisValues: [0, Math.max(...getCurrentYearData().map(item => item.value), 1) / 2, Math.max(...getCurrentYearData().map(item => item.value), 1)]
            };
            title = getYearlyTitle();
            activeViewStr = "year";
            onPrevious = handlePreviousYear;
            onNext = handleNextYear;
        }

        return (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* 네비게이션 */}
                <DateIndicator
                    title={title}
                    activeView={activeViewStr}
                    onPreviousWeek={onPrevious}
                    onPreviousMonth={onPrevious}
                    onPreviousYear={onPrevious}
                    onNextWeek={onNext}
                    onNextMonth={onNext}
                    onNextYear={onNext}
                />

                {/* 메인 콘텐츠 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* 좌측 요약 패널 */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* 통계 섹션 */}
                        <CommonCard>
                            {/* 평균 디딤 수 */}
                            <Box sx={{ width: '100%', mb: 2 }}>
                                <DataItem
                                    value={stats?.value?.toLocaleString()}
                                    unit={t('didim_day')}
                                    size="large"
                                    sx={{ justifyContent: "left" }}
                                />
                            </Box>

                            {/* 통계 섹션 */}
                            <StatsSection
                                stats={{
                                    totalSteps: stats?.totalSteps,
                                    bestDay: stats?.bestDay,
                                    worstDay: stats?.worstDay
                                }}
                                activeView={activeViewStr}
                                type="didim"
                            />
                        </CommonCard>
                    </Box>

                    {/* 우측 바 차트 */}
                    <Box sx={{ flex: 1 }}>
                        <CommonCard>
                            <StatisticsChart
                                activeView={activeViewStr}
                                chartData={chartData}
                                yAxisData={yAxisData}
                                selectedBarIndex={selectedBarIndex}
                                onBarSelect={setSelectedBarIndex}
                            />
                        </CommonCard>
                    </Box>
                </Box>
            </Box>
        );
    };


    const allDaysData = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

        const days = [];

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364); // 1년 전 (365일)
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);
            date.setHours(0, 0, 0, 0);

            // 날짜 비교 시 시간 부분 제거
            const dateForCompare = new Date(date);
            dateForCompare.setHours(0, 0, 0, 0);
            const selectedDateForCompare = new Date(selectedDate);
            selectedDateForCompare.setHours(0, 0, 0, 0);
            const isSelected = dateForCompare.getTime() === selectedDateForCompare.getTime();

            // 해당 날짜의 실제 데이터 찾기 (이미 필터링된 filteredPlayLogsData 사용)
            const dayRecords = filteredPlayLogsData?.filter(record =>
                record.time && formatDateTime(new Date(record.time), '-', ':', 0, 10) === formatDateTime(date, '-', ':', 0, 10)
            );

            // 데이터 합산
            const dayRecordsArray = dayRecords || [];
            const dayTotals = dayRecordsArray.reduce((acc, record) => {
                return {
                    stepCount: acc.stepCount + (record.stepCount || 0),
                    calories: acc.calories + (Math.max(0, (record.endCalories || 0) - (record.startCalories || 0))),
                    playTime: acc.playTime + (Math.max(0, (record.endPlayTime || 0) - (record.startPlayTime || 0)))
                };
            }, {
                stepCount: 0,
                calories: 0,
                playTime: 0
            });

            const chartData = [
                {
                    label: t('didim'),
                    value: calculatePercentage(
                        dayTotals.stepCount || 0,
                        EXERCISE_TARGETS.STEPS
                    ),
                    color: theme.palette.figma.didim,
                    radius: 25,
                    strokeWidth: 5,
                },
                {
                    label: t('calories'),
                    value: calculatePercentage(
                        dayTotals.calories || 0,
                        EXERCISE_TARGETS.CALORIES
                    ),
                    color: theme.palette.figma.calorieRed,
                    radius: 20,
                    strokeWidth: 5,
                },
                {
                    label: t('time'),
                    value: calculatePercentage(
                        dayTotals.playTime || 0,
                        EXERCISE_TARGETS.ACTIVITY_TIME * 60 // 초 단위로 변환
                    ),
                    color: theme.palette.figma.time,
                    radius: 15,
                    strokeWidth: 5,
                },
            ];

            days.push({
                day: dayNames[date.getDay()],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
                stepCount: dayTotals?.stepCount,
                calories: dayTotals?.calories,
                playTime: dayTotals?.playTime,
                hourlySteps: dayRecords?.[0]?.hourlySteps,
                hourlyCalories: dayRecords?.[0]?.hourlyCalories,
                hourlyPlayTime: dayRecords?.[0]?.hourlyPlayTime
            });
        }

        return days; // 가장 오래된 날짜부터 오늘까지 순서 (오늘이 배열의 마지막 = 오른쪽 끝)
    }, [selectedDate, filteredPlayLogsData, theme.palette.figma, t]);

    // 선택된 날짜 계산 (뷰에 따라 다름)
    const selectedDateForStats = useMemo(() => {
        if (activeView === 0) {
            // 일간: selectedDate 사용
            return selectedDate;
        } else if (activeView === 1) {
            // 주간: 선택된 바의 날짜
            if (selectedBarIndex >= 0 && selectedBarIndex < getCurrentWeekData().length) {
                const selectedDay = getCurrentWeekData()[selectedBarIndex].day;
                return new Date(selectedDay);
            }
            // 선택되지 않았으면 주간의 첫 번째 날짜
            if (getCurrentWeekData().length > 0) {
                return new Date(getCurrentWeekData()[0].day);
            }
            return selectedDate;
        } else if (activeView === 2) {
            // 월간: 선택된 바의 날짜
            if (selectedBarIndex >= 0 && selectedBarIndex < getCurrentMonthData().length) {
                const selectedDay = getCurrentMonthData()[selectedBarIndex].day;
                return new Date(selectedDay);
            }
            // 선택되지 않았으면 오늘 날짜
            return new Date();
        } else if (activeView === 3) {
            // 연간: 선택된 바의 월의 첫 번째 날짜
            if (selectedBarIndex >= 0 && selectedBarIndex < getCurrentYearData().length) {
                const selectedMonth = getCurrentYearData()[selectedBarIndex].day;
                return new Date(selectedMonth + '-01');
            }
            // 선택되지 않았으면 현재 월의 첫 번째 날짜
            return new Date();
        }
        return selectedDate;
    }, [activeView, selectedDate, selectedBarIndex, getCurrentMonthData, getCurrentWeekData, getCurrentYearData]);

    // 선택된 날짜의 데이터 가져오기
    const getSelectedDateData = (date) => {
        const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD 형식
        const dayRecords = playLogsData?.filter(record =>
            record.time && record.time.startsWith(dateStr)
        ) || [];

        const dayTotals = dayRecords.reduce((acc, record) => {
            return {
                totalSteps: acc.totalSteps + (record.stepCount || 0),
                calories: acc.calories + (Math.max(0, (record.endCalories || 0) - (record.startCalories || 0))),
                totalTime: acc.totalTime + (Math.max(0, (record.endPlayTime || 0) - (record.startPlayTime || 0)))
            };
        }, {
            totalSteps: 0,
            calories: 0,
            totalTime: 0
        });

        return dayTotals;
    };

    // 선택된 날짜의 skill 데이터 계산
    // 해당 날짜의 playLogs의 contentIndex와 맞는 contents 데이터에서 체력 지표 합산
    const selectedDateSkillData = useMemo(() => {
        if (activeView !== 0) {
            // 일간 뷰가 아니면 기본 skill 사용
            return;
        }

        // selectedDate의 시간 부분을 제거하여 날짜만 비교
        const selectedDateForCompare = new Date(selectedDate);
        selectedDateForCompare.setHours(0, 0, 0, 0);
        const dateStr = formatDateTime(selectedDateForCompare, '-', ':', 0, 10); // YYYY-MM-DD 형식

        // 선택된 날짜의 playLogsData 필터링 (시간 부분 제거하여 날짜만 비교)
        const dayRecords = filteredPlayLogsData?.filter(record => {
            if (!record.time) return false;
            const recordDate = new Date(record.time);
            recordDate.setHours(0, 0, 0, 0);
            const recordDateStr = formatDateTime(recordDate, '-', ':', 0, 10);
            return recordDateStr === dateStr;
        }) || [];

        if (dayRecords.length > 0 && contents.length > 0) {
            // dayRecords에서 contentIndex 추출
            const contentIndexes = dayRecords
                .map(record => record.contentIndex)
                .filter(index => index != null);

            // 해당 contentIndex와 맞는 contents 데이터 찾기
            const matchedContents = contents.filter(content =>
                contentIndexes.includes(content.index)
            );

            if (matchedContents.length > 0) {
                // 각 콘텐츠의 체력 지표 합산
                const skillTotals = matchedContents.reduce((acc, content) => {
                    return {
                        endurance: acc.endurance + (content.endurance || 0),
                        strength: acc.strength + (content.strength || 0),
                        balance: acc.balance + (content.balance || 0),
                        flexibility: acc.flexibility + (content.flexibility || 0),
                        reaction: acc.reaction + (content.reaction || 0),
                        cooperation: acc.cooperation + (content.cooperation || 0),
                    };
                }, {
                    endurance: 0,
                    strength: 0,
                    balance: 0,
                    flexibility: 0,
                    reaction: 0,
                    cooperation: 0,
                });

                // 합산된 값 반환 (Radar chart에 표시됨)
                return {
                    endurance: skillTotals.endurance,
                    strength: skillTotals.strength,
                    balance: skillTotals.balance,
                    flexibility: skillTotals.flexibility,
                    reaction: skillTotals.reaction,
                    cooperation: skillTotals.cooperation,
                };
            }
        }

    }, [selectedDate, filteredPlayLogsData, activeView, contents]);

    // 오늘 날짜인지 확인
    const isToday = useMemo(() => {
        const today = new Date();
        const selected = new Date(selectedDate);
        today.setHours(0, 0, 0, 0);
        selected.setHours(0, 0, 0, 0);
        return today.getTime() === selected.getTime();
    }, [selectedDate]);

    // 현재 선택된 날짜의 실제 데이터 (allDaysData에서 가져오기 또는 계산)
    const currentDayData = useMemo(() => {
        let selectedDay;

        if (activeView === 0) {
            // 일간: allDaysData에서 찾기
            selectedDay = allDaysData.find(day =>
                formatDateTime(day.date, '/', ':', 0, 10) === formatDateTime(selectedDate, '/', ':', 0, 10)
            );
        } else {
            // 주간/월간/연간: 선택된 날짜의 데이터 계산
            const dayTotals = getSelectedDateData(selectedDateForStats);
            return [
                {
                    value: dayTotals.stepCount || 0,
                    color: theme.palette.figma.didim,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    value: dayTotals.calories || 0,
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    value: dayTotals.playTime || 0,
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];
        }

        if (selectedDay) {
            // 실제 데이터를 사용하여 chartData 생성
            return [
                {
                    value: selectedDay.stepCount || 0,
                    color: theme.palette.figma.didim,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    value: selectedDay.calories || 0,
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    value: selectedDay.playTime || 0,
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];
        }

        // 데이터가 없으면 기본값 반환
        return [
            {
                value: 0,
                color: theme.palette.figma.didim,
                radius: 15,
                strokeWidth: 3,
            },
            {
                value: 0,
                color: theme.palette.figma.calorieRed,
                radius: 12,
                strokeWidth: 3,
            },
            {
                value: 0,
                color: theme.palette.figma.time,
                radius: 9,
                strokeWidth: 3,
            },
        ];
    }, [allDaysData, selectedDate, selectedDateForStats, activeView, playLogsData, theme.palette.figma, getSelectedDateData]);

    // 시간별 디딤수 데이터 생성 (playLogsData 기준)
    const hourlyStepData = useMemo(() => {
        if (!playLogsData || playLogsData.length === 0) {
            return Array(24).fill(0);
        }

        const selectedDateStr = formatDateTime(selectedDate, '-', ':', 0, 10); // YYYY-MM-DD 형식
        const dayLogs = playLogsData.filter(log => {
            const logDate = new Date(log.time);
            const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);

            return logDateStr === selectedDateStr;
        });

        // 시간대별 데이터 초기화 (0-23시)
        const hourlyData = new Array(24).fill(0);

        // 시간대별 디딤수 집계
        dayLogs.forEach(log => {
            const logDate = new Date(log.time);
            const hour = logDate.getHours(); // 0-23
            if (hour >= 0 && hour < 24) {
                hourlyData[hour] += (log.stepCount || 0);
            }
        });

        return hourlyData;
    }, [playLogsData, selectedDate]);

    // 시간별 심박수 데이터 생성 (playLogsData 기준)
    const hourlyHeartbeatData = useMemo(() => {
        if (!playLogsData || playLogsData.length === 0) {
            return new Array(24).fill([0, 0, 0]); // [min, max, avg]
        }

        const selectedDateStr = formatDateTime(selectedDate, '-', ':', 0, 10); // YYYY-MM-DD 형식
        const dayLogs = playLogsData.filter(log => {
            const logDate = new Date(log.time);
            const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
            return logDateStr === selectedDateStr;
        });

        // 시간대별 데이터 초기화 (0-23시)
        const hourlyData = new Array(24).fill(null).map(() => ({ min: [], max: [], avg: [] }));

        // 시간대별 심박수 데이터 집계
        dayLogs.forEach(log => {
            const logDate = new Date(log.time);
            const hour = logDate.getHours(); // 0-23
            if (hour >= 0 && hour < 24) {
                // playLogsData에서 심박수 필드 확인
                if (log.minHeartRate != null) hourlyData[hour].min.push(log.minHeartRate);
                if (log.maxHeartRate != null) hourlyData[hour].max.push(log.maxHeartRate);
                if (log.avgHeartRate != null) hourlyData[hour].avg.push(log.avgHeartRate);
            }
        });

        // 시간대별로 min, max, avg 계산
        return hourlyData.map(hourData => {
            const min = hourData.min.length > 0 ? Math.min(...hourData.min) : 0;
            const max = hourData.max.length > 0 ? Math.max(...hourData.max) : 0;
            const avg = hourData.avg.length > 0 ? Math.round(hourData.avg.reduce((a, b) => a + b, 0) / hourData.avg.length) : 0;
            return [min, max, avg];
        });
    }, [playLogsData, selectedDate]);

    // 선택된 시간대 데이터 계산
    const selectedHourData = selectedHourIndex >= 0 ? hourlyHeartbeatData[selectedHourIndex] : [0, 0, 0];
    const selectedHour = selectedHourIndex >= 0 ? selectedHourIndex : 0;
    const selectedHourRange = `${selectedHour.toString().padStart(2, '0')}:00 - ${selectedHour.toString().padStart(2, '0')}:59`;
    const selectedHeartbeatRange = selectedHourIndex >= 0 && selectedHourData && selectedHourData[0] !== 0 && selectedHourData[1] !== 0
        ? `${selectedHourData[0]} - ${selectedHourData[1]} bmp`
        : '-';

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 상단 컨트롤 영역 */}
            <Toggle
                options={[t('daily'), t('weekly'), t('monthly'), t('yearly')]}
                value={activeView}
                onChange={(newView) => {
                    setActiveView(newView);
                    clearSelectedBar();
                }}
            />

            {/* 뷰별 콘텐츠 렌더링 */}
            {activeView === 0 ? (
                <>
                    {/* 주간 날짜 선택기 with week swipe */}
                    <WeekCalendar
                        days={days}
                        onDayClick={handleDayClick}
                        size="large"
                        enableWeekSwipe={true}
                        onWeekChange={handleWeekChange}
                        selectedDate={selectedDate}
                        enableScroll={true}
                        allDaysData={allDaysData}
                        isLoading={false}
                        onScrollToEnd={async (direction) => {
                            // 스크롤로 더 많은 데이터 로드
                            if (!userData?.id || direction !== 'past') return;
                            const sortedLogs = [...(filteredPlayLogsData || [])].sort((a, b) => {
                                const dateA = new Date(a.time || a.startTime);
                                const dateB = new Date(b.time || b.startTime);
                                return dateA - dateB;
                            });
                            if (sortedLogs.length === 0) return;
                            const earliestDate = new Date(sortedLogs[0].time || sortedLogs[0].startTime);
                            earliestDate.setHours(0, 0, 0, 0);
                            const newStartDate = new Date(earliestDate);
                            newStartDate.setDate(newStartDate.getDate() - 14);
                            newStartDate.setHours(0, 0, 0, 0);
                            const newEndDate = new Date(earliestDate);
                            newEndDate.setDate(newEndDate.getDate() - 1);
                            newEndDate.setHours(23, 59, 59, 999);
                            const startTime = newStartDate.toISOString();
                            const endTime = newEndDate.toISOString();
                            try {
                                let allLogs = [];
                                let offset = 0;
                                let page;
                                do {
                                    const response = await logAPI.getPlayLogs({
                                        userId: userData.id,
                                        startTime,
                                        endTime,
                                        expand: 'organization',
                                        offset
                                    });
                                    page = response?.data?.logs || [];
                                    allLogs = allLogs.concat(page);
                                    offset += API_PAGE_SIZE;
                                } while (page.length === API_PAGE_SIZE);
                                if (allLogs.length > 0) {
                                    setLocalPlayLogsData(prevLogs => {
                                        const existingIds = new Set((prevLogs || []).map(log => log.id));
                                        const uniqueNewLogs = allLogs.filter(log => !existingIds.has(log.id));
                                        return [...(prevLogs || []), ...uniqueNewLogs].sort((a, b) => {
                                            const dateA = new Date(a.time || a.startTime);
                                            const dateB = new Date(b.time || b.startTime);
                                            return dateA - dateB;
                                        });
                                    });
                                }
                            } catch (err) {
                                console.error('플레이 로그 데이터 로드 실패:', err);
                            }
                        }}
                    />

                    {/* 메인 콘텐츠 */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* 좌측 메인 영역 */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* 일일 요약 카드 */}
                            <CommonCard>
                                {/* 날짜 헤더 */}
                                <Typography sx={{
                                    color: theme.palette.figma.white[80],
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    letterSpacing: '-0.48px',
                                    lineHeight: 1,
                                    textAlign: 'left',
                                    alignSelf: 'stretch',
                                    mb: 2
                                }}>
                                    {`${isToday ? t('today') + ' ' : ''}${formatDateTimeDisplay(selectedDate, 'monthDay')}`}
                                </Typography>

                                {/* 메인 통계 */}
                                <Box sx={{
                                    display: 'flex',
                                    gap: '20px',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    {/* 왼쪽 통계 */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '10px',
                                        flex: 1
                                    }}>
                                        {/* 디딤 */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DidimIcon />
                                            <Typography variant="body1">
                                                {currentDayData[0]?.value || 0} {t('didim')}
                                            </Typography>
                                        </Box>

                                        {/* 칼로리 */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CaloryIcon />
                                            <Typography variant="body1">
                                                {currentDayData[1]?.value || 0} kcal
                                            </Typography>
                                        </Box>

                                        {/* 시간 */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TimeIcon />
                                            <Typography variant="body1">
                                                {Math.floor((currentDayData[2]?.value || 0) / 60)}{t('min')} {(currentDayData[2]?.value || 0) % 60}{t('sec')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* 오른쪽 차트 영역 */}
                                    <Box sx={{ width: '100px', height: '100px' }}>
                                        <DonutChart data={[
                                            {
                                                label: t('didim'),
                                                value: calculatePercentage(
                                                    currentDayData[0]?.value || 0,
                                                    EXERCISE_TARGETS.STEPS
                                                ),
                                                color: theme.palette.figma.didim,
                                                radius: 45,
                                                strokeWidth: 10,
                                            },
                                            {
                                                label: t('calories'),
                                                value: calculatePercentage(
                                                    currentDayData[1]?.value || 0,
                                                    EXERCISE_TARGETS.CALORIES
                                                ),
                                                color: theme.palette.figma.calorieRed,
                                                radius: 35,
                                                strokeWidth: 10,
                                            },
                                            {
                                                label: t('time'),
                                                value: calculatePercentage(
                                                    currentDayData[2]?.value || 0,
                                                    EXERCISE_TARGETS.ACTIVITY_TIME * 60
                                                ),
                                                color: theme.palette.figma.time,
                                                radius: 25,
                                                strokeWidth: 10,
                                            },
                                        ]} size={100} centerRadius={60} />
                                    </Box>
                                </Box>
                            </CommonCard>
                            {/* 체력 레이더 차트 카드 */}
                            <CommonCard>
                                <RadarChart
                                    data={{
                                        endurance: selectedDateSkillData?.endurance || 0, // 심폐지구력
                                        strength: selectedDateSkillData?.strength, // 근력근지구력
                                        balance: selectedDateSkillData?.balance, // 평형성
                                        flexibility: selectedDateSkillData?.flexibility, // 유연성
                                        reaction: selectedDateSkillData?.reaction, // 순발력
                                        cooperation: selectedDateSkillData?.cooperation, // 협응력
                                    }}
                                    size={100}
                                    showLabels={true}
                                    showBackground={true}
                                    sx={{ width: 140, height: 160 }}
                                />
                            </CommonCard>
                            {/* 당일일 때만 운동추천AI 표시 */}
                            {isToday && userData?.recommendation && userData?.recommendation?.length > 0 && (
                                <Recommendation
                                    data={userData?.recommendation}
                                    userName={userData?.name || ''}
                                    yesterdaySteps={yesterdayPhysicalRecord?.totalSteps || null}
                                    avgHeartRate={yesterdayPhysicalRecord?.avgHeartRate || null}
                                />
                            )}
                        </Box>

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <CommonCard sx={{ alignItems: 'flex-start' }}>
                                <ChartSection
                                    title={t('hourly_didim_count')}
                                    renderChart={<BarChart
                                        data={hourlyStepData}
                                        color={theme.palette.figma.didim}
                                        maxHeight={100}
                                        currentDate={formatDateTimeDisplay(selectedDate, 'date')}
                                        clickable={true}
                                        selectedHourIndex={selectedHourIndex}
                                        onHourSelect={setSelectedHourIndex}
                                    />}
                                />
                            </CommonCard>

                            <CommonCard sx={{ alignItems: 'flex-start' }}>
                                <ChartSection
                                    title={t('hourly_min_max_heart_rate')}
                                    renderChart={
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '10px',
                                            width: '100%'
                                        }}>
                                            {/* 차트 영역 */}
                                            <Box sx={{
                                                display: 'flex',
                                                gap: '5px',
                                                alignItems: 'flex-start',
                                                width: '100%',
                                                position: 'relative'
                                            }}>
                                                {/* 메인 차트 영역 */}
                                                <Box sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '3px',
                                                    overflow: 'hidden'
                                                }}>
                                                    {/* 선택된 시간대 팝업 - selectedHourIndex가 유효할 때만 표시 */}
                                                    {selectedHourIndex >= 0 && (
                                                        <Box sx={{
                                                            height: `${popupHeight}px`,
                                                            position: 'absolute',
                                                            width: '100%'
                                                        }}>
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                left: (() => {
                                                                    // BarChart와 동일한 위치 계산 로직 사용
                                                                    const totalBars = 24;
                                                                    const barHalfWidthPercent = 1.67;
                                                                    const startPosition = barHalfWidthPercent;
                                                                    const endPosition = 100 - barHalfWidthPercent;
                                                                    const range = endPosition - startPosition;
                                                                    let _index = selectedHourIndex;

                                                                    // 가장자리 처리
                                                                    if (selectedHourIndex <= 2) {
                                                                        _index = 3;
                                                                    } else if (selectedHourIndex >= 19) {
                                                                        _index = 18;
                                                                    }

                                                                    const barCenter = startPosition + (_index / (totalBars - 1)) * range;
                                                                    return `${barCenter}%`;
                                                                })(),
                                                                top: 0,
                                                                display: 'flex',
                                                                gap: '5px',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                padding: '5px 10px',
                                                                backgroundColor: theme.palette.figma.heartRateDark,
                                                                borderRadius: '10px',
                                                                zIndex: 1,
                                                                transform: 'translateX(-50%)'
                                                            }}>
                                                                <Typography sx={{
                                                                    color: theme.palette.figma.white[80],
                                                                    fontSize: '12px',
                                                                    fontWeight: 400,
                                                                    letterSpacing: '-0.36px',
                                                                    lineHeight: 1,
                                                                    textAlign: 'right',
                                                                    whiteSpace: 'nowrap',
                                                                    zIndex: 3
                                                                }}>
                                                                    {selectedHourRange}
                                                                </Typography>
                                                                <Typography sx={{
                                                                    color: theme.palette.figma.heartRate,
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    letterSpacing: '-0.42px',
                                                                    lineHeight: 1,
                                                                    textAlign: 'right',
                                                                    whiteSpace: 'nowrap',
                                                                    zIndex: 2
                                                                }}>
                                                                    {selectedHeartbeatRange}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* 24시간 바 차트 */}
                                                    <Box sx={{
                                                        height: '100px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        position: 'relative',
                                                        width: '100%'
                                                    }}>
                                                        {/* Y축 그리드 라인 */}
                                                        {/* 140 그리드 라인 */}
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            right: '20px',
                                                            top: '44px', // 140 위치 ((CHALLENGE_HEARTBEAT_MAX-140)/180)*100 = 44px
                                                            height: '1px',
                                                            backgroundColor: theme.palette.figma.white[30],
                                                            zIndex: 0
                                                        }} />

                                                        {/* 140 라벨 */}
                                                        <Typography sx={{
                                                            position: 'absolute',
                                                            right: '0px',
                                                            top: '40px', // 44px - 4px (라벨 중앙 정렬)
                                                            color: theme.palette.figma.white[60],
                                                            fontSize: '10px',
                                                            fontWeight: 500,
                                                            letterSpacing: '-0.3px',
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 1
                                                        }}>
                                                            140
                                                        </Typography>

                                                        {/* 40 그리드 라인 */}
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            right: '20px',
                                                            bottom: '0px', // 40 위치 (맨 아래)
                                                            height: '1px',
                                                            backgroundColor: theme.palette.figma.white[30],
                                                            zIndex: 0
                                                        }} />

                                                        {/* 40 라벨 */}
                                                        <Typography sx={{
                                                            position: 'absolute',
                                                            right: '0px',
                                                            bottom: '-4px', // 0px - 4px (라벨 중앙 정렬)
                                                            color: theme.palette.figma.white[60],
                                                            fontSize: '10px',
                                                            fontWeight: 400,
                                                            letterSpacing: '-0.3px',
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 1
                                                        }}>
                                                            40
                                                        </Typography>

                                                        {/* 24개 바 생성 */}
                                                        {hourlyHeartbeatData.map((heartbeatData, index) => {
                                                            const [min, max] = heartbeatData;

                                                            // Y축 범위 40-CHALLENGE_HEARTBEAT_MAX 차트 전체 높이(100px)에 매핑
                                                            const yAxisMin = 40;
                                                            const yAxisMax = CHALLENGE_HEARTBEAT_MAX;
                                                            const yAxisRange = yAxisMax - yAxisMin; // 180

                                                            // 바의 상단 위치 계산 (max 값 기준)
                                                            // CHALLENGE_HEARTBEAT_MAX 0px(top), 40은 100px(bottom)에 위치
                                                            const maxPosition = ((yAxisMax - max) / yAxisRange) * 100; // CHALLENGE_HEARTBEAT_MAX에서 max까지의 거리
                                                            const minPosition = ((yAxisMax - min) / yAxisRange) * 100; // CHALLENGE_HEARTBEAT_MAX에서 min까지의 거리

                                                            const barTop = maxPosition; // 0px에서 시작해서 아래로
                                                            const barHeight = minPosition - maxPosition;

                                                            // X축에 맞춰 바 위치 계산 (BarChart와 동일한 로직 사용)
                                                            const totalBars = 24;
                                                            const barHalfWidthPercent = 1.67; // 바 너비의 절반 (BarChart와 동일)
                                                            const startPosition = barHalfWidthPercent; // 1.67%
                                                            const endPosition = 100 - barHalfWidthPercent; // 98.33%
                                                            const range = endPosition - startPosition; // 96.66%
                                                            const barCenter = startPosition + (index / (totalBars - 1)) * range;
                                                            const barWidth = 3.4; // 바 너비
                                                            const barLeft = barCenter - (barWidth / 2); // 바의 중심에서 왼쪽으로 절반 이동

                                                            if (min === 0 && max === 0) {
                                                                return (
                                                                    <Box key={index} sx={{
                                                                        height: '100px',
                                                                        width: `${barWidth}%`,
                                                                    }} />
                                                                );
                                                            }

                                                            return (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        height: '100px',
                                                                        width: `${barWidth}%`,
                                                                        position: 'absolute',
                                                                        left: `${barLeft}%`,
                                                                        zIndex: 25,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onClick={() => setSelectedHourIndex(index)}
                                                                >
                                                                    <Box sx={{
                                                                        display: (selectedHourIndex >= 0 && selectedHourIndex === index) ? 'flex' : 'none',
                                                                        position: 'absolute',
                                                                        left: `50%`, // 실제 바차트 위치 기준
                                                                        top: `${popupHeight}px`,
                                                                        width: '1px', // 가로너비 1px
                                                                        height: `${barTop - popupHeight}px`,
                                                                        backgroundColor: theme.palette.figma.heartRateDark,
                                                                    }} />
                                                                    <Box sx={{
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        top: `${barTop}px`,
                                                                        width: '100%',
                                                                        height: `${barHeight}px`,
                                                                        backgroundColor: theme.palette.figma.heartRate,
                                                                        borderRadius: '4px',
                                                                    }} />
                                                                </Box>
                                                            );
                                                        })}


                                                    </Box>

                                                    {/* X축 시간 표시 */}
                                                    <Box sx={{
                                                        height: '4px',
                                                        display: 'flex',
                                                        alignItems: 'flex-end',
                                                        position: 'relative',
                                                        width: '100%'
                                                    }}>
                                                        {[0, 6, 12, 18].map((hour) => {
                                                            // BarChart와 동일한 위치 계산 로직 사용
                                                            const totalBars = 24;
                                                            const barHalfWidthPercent = 1.67;
                                                            const startPosition = barHalfWidthPercent;
                                                            const endPosition = 100 - barHalfWidthPercent;
                                                            const range = endPosition - startPosition;
                                                            const barCenter = startPosition + (hour / (totalBars - 1)) * range;

                                                            return (
                                                                <Box key={hour} sx={{
                                                                    position: 'absolute',
                                                                    left: `${barCenter}%`,
                                                                    transform: 'translateX(-50%)',
                                                                    height: '4px',
                                                                    width: '1px',
                                                                    backgroundColor: theme.palette.figma.white[30],
                                                                    borderRadius: '2px'
                                                                }} />
                                                            );
                                                        })}
                                                    </Box>

                                                    {/* X축 라벨 */}
                                                    <Box sx={{
                                                        height: '14px',
                                                        display: 'flex',
                                                        alignItems: 'flex-end',
                                                        position: 'relative',
                                                        width: '100%'
                                                    }}>
                                                        {[0, 6, 12, 18].map((hour) => {
                                                            // BarChart와 동일한 위치 계산 로직 사용
                                                            const totalBars = 24;
                                                            const barHalfWidthPercent = 1.67;
                                                            const startPosition = barHalfWidthPercent;
                                                            const endPosition = 100 - barHalfWidthPercent;
                                                            const range = endPosition - startPosition;
                                                            const barCenter = startPosition + (hour / (totalBars - 1)) * range;

                                                            return (
                                                                <Box key={hour} sx={{
                                                                    position: 'absolute',
                                                                    left: `${barCenter}%`,
                                                                    transform: 'translateX(-50%)'
                                                                }}>
                                                                    <Typography sx={{
                                                                        color: theme.palette.figma.white[60],
                                                                        fontSize: '12px',
                                                                        fontWeight: 400,
                                                                        letterSpacing: '-0.36px',
                                                                        lineHeight: 1,
                                                                        textAlign: 'center',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {`${hour.toString().padStart(2, '0')}:00`}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>

                                            </Box>

                                        </Box>
                                    }
                                />
                            </CommonCard>

                            {/* 운동 활동 리스트 */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                width: '100%'
                            }}>
                                {selectedDateActivityList && selectedDateActivityList.length > 0 ? (
                                    selectedDateActivityList
                                        .slice(0, 4) // 최대 4개만 표시
                                        .map((log, index) => {
                                            // 시간 표시
                                            const logDate = new Date(log.time);
                                            const timeStr = formatDateTimeDisplay(logDate, 'time');
                                            const hours = logDate.getHours();
                                            const period = hours < 12 ? t('am') : t('pm');
                                            const timeDisplay = `${period} ${timeStr}`;

                                            // 시간 계산 (startTime과 endTime의 차이, 초를 mm:ss 형식으로)
                                            let duration = 0;
                                            if (log.startTime && log.endTime) {
                                                const start = new Date(log.startTime);
                                                const end = new Date(log.endTime);
                                                duration = Math.floor((end - start) / 1000); // 초 단위
                                            } else {
                                                duration = log.duration || 0;
                                            }
                                            const minutes = Math.floor(duration / 60);
                                            const seconds = duration % 60;
                                            const timeValue = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

                                            // 콘텐츠 정보 찾기
                                            const content = contents.find(c => c.index === log.contentIndex);

                                            // 콘텐츠 이름 또는 기본값
                                            const contentName = content?.name || log.gameName || log.contentName || t('exercise_activity');

                                            // 아이콘 (콘텐츠에서 가져오거나 기본값)
                                            const icon = content?.icon || log.icon || '🏃';

                                            // 점수 표시
                                            const score = log.score ? `${log.score}점` : null;

                                            return (
                                                <ActivityCard
                                                    key={log.id || index}
                                                    icon={icon}
                                                    title={contentName}
                                                    time={timeDisplay}
                                                    score={score}
                                                    didimValue={log.stepCount || 0}
                                                    calorieValue={log.calories || log.endCalories - log.startCalories || 0}
                                                    timeValue={timeValue}
                                                />
                                            );
                                        })
                                ) : (
                                    <Typography sx={{
                                        color: theme.palette.figma.white[60],
                                        fontSize: '14px',
                                        textAlign: 'center',
                                        padding: '20px'
                                    }}>
                                        {t('no_activity_data')}
                                    </Typography>
                                )}
                            </Box>

                        </Box>
                    </Box>
                </>
            ) : (
                renderRangeView()
            )}
        </Box>
    );
};
